import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, ArrowLeft, Mail, CheckCircle2 } from "lucide-react";

const ForgotPassword = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;
      setIsSuccess(true);
    } catch (err: any) {
      console.error("Reset password error:", err);
      setError(err.message || t("เกิดข้อผิดพลาด กรุณาลองใหม่", "An error occurred. Please try again."));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
      <div className="w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("กลับไปหน้าเข้าสู่ระบบ", "Back to login")}
        </Link>

        <Card className="border-border/50 shadow-xl">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Mail className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-2xl">
              {t("ลืมรหัสผ่าน", "Forgot Password")}
            </CardTitle>
            <CardDescription>
              {t(
                "กรอกอีเมลของคุณ เราจะส่งลิงก์สำหรับรีเซ็ตรหัสผ่านไปให้",
                "Enter your email and we'll send you a link to reset your password"
              )}
            </CardDescription>
          </CardHeader>

          <CardContent>
            {isSuccess ? (
              <div className="text-center py-6">
                <div className="mx-auto w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle2 className="h-8 w-8 text-green-500" />
                </div>
                <h3 className="text-lg font-semibold mb-2">
                  {t("ส่งอีเมลแล้ว!", "Email Sent!")}
                </h3>
                <p className="text-muted-foreground text-sm mb-6">
                  {t(
                    "เราได้ส่งลิงก์รีเซ็ตรหัสผ่านไปที่อีเมลของคุณแล้ว กรุณาตรวจสอบกล่องจดหมาย (และโฟลเดอร์สแปม)",
                    "We've sent a password reset link to your email. Please check your inbox (and spam folder)."
                  )}
                </p>
                <p className="text-xs text-muted-foreground mb-4">
                  {email}
                </p>
                <Link to="/login">
                  <Button variant="outline" className="w-full">
                    {t("กลับไปหน้าเข้าสู่ระบบ", "Back to login")}
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {t("อีเมล", "Email")}
                  </label>
                  <Input
                    type="email"
                    placeholder={t("กรอกอีเมลของคุณ", "Enter your email")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                  />
                </div>

                {error && (
                  <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-lg">
                    {error}
                  </div>
                )}

                <Button type="submit" className="w-full" disabled={isLoading || !email}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("กำลังส่ง...", "Sending...")}
                    </>
                  ) : (
                    t("ส่งลิงก์รีเซ็ตรหัสผ่าน", "Send Reset Link")
                  )}
                </Button>

                <p className="text-center text-sm text-muted-foreground">
                  {t("จำรหัสผ่านได้แล้ว?", "Remember your password?")}{" "}
                  <Link to="/login" className="text-primary hover:underline">
                    {t("เข้าสู่ระบบ", "Sign in")}
                  </Link>
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
